// The widget types a merchandiser can author in the CMS. Keep in sync with
// apps/api/routes/merchandising/models.py WIDGET_TYPES and the
// widget.widget_type CHECK (migrations 49, 57, 67, 76, 79, 85).
//
// Card carousels are four first-class widget types (migration 85 promoted them
// from the old coarse `carousel` marker + metadata.carousel_type). The
// size/orientation variant now lives in the widget_type itself; each authors
// with the same card-rail config (see CarouselEditor).
//
// 'hero_banner' is its own widget_type since migration 79; its slides are
// authored in HeroBannerEditor and stored in metadata.slides.

// Which widget types are valid on which site. The two product lines have
// mostly-disjoint catalogues (docs/html/tennischannel/widget-inventory.html):
// tennis.com gets the four editorial/web widgets; Tennis Channel gets the
// carousel and hero banner. `sites` holds site.code values
// (shared/migrations/49_merchandising.sql). editorial is the one shared type —
// Decision F resolved to share it (markdown prose isn't surface-specific), so
// it authors on both sites.
export const WIDGET_TYPES = [
  { code: 'video_embed', label: 'Video Embed', sites: ['tennis_com'] },
  { code: 'editorial', label: 'Editorial', sites: ['tennis_com', 'tennis_channel'] },
  { code: 'banner_image', label: 'Banner Image', sites: ['tennis_com'] },
  { code: 'google_ad_slot', label: 'Google Ad Slot', sites: ['tennis_com'] },
  { code: 'standard_landscape_carousel', label: 'Standard Landscape Carousel', sites: ['tennis_channel'] },
  { code: 'standard_portrait_carousel', label: 'Standard Portrait Carousel', sites: ['tennis_channel'] },
  { code: 'small_landscape_carousel', label: 'Small Landscape Carousel', sites: ['tennis_channel'] },
  { code: 'large_landscape_carousel', label: 'Large Landscape Carousel', sites: ['tennis_channel'] },
  { code: 'hero_banner', label: 'Hero Banner', sites: ['tennis_channel'] },
] as const

export type WidgetTypeCode = typeof WIDGET_TYPES[number]['code']

// The card-carousel widget types. All four share the card-rail authoring
// config (CarouselEditor); the widget_type alone carries the card size and
// orientation. Keep in sync with apps/api/routes/merchandising/models.py
// CAROUSEL_WIDGET_TYPES.
export const CAROUSEL_WIDGET_TYPES = [
  'standard_landscape_carousel',
  'standard_portrait_carousel',
  'small_landscape_carousel',
  'large_landscape_carousel',
] as const

export type CarouselWidgetTypeCode = typeof CAROUSEL_WIDGET_TYPES[number]

export const isCarouselWidgetType = (code: string): code is CarouselWidgetTypeCode =>
  (CAROUSEL_WIDGET_TYPES as readonly string[]).includes(code)

// Short display label for a widget-type badge (placements list, widget library,
// placement detail). The four carousel variants collapse to a single "Carousel"
// — the size/orientation distinction is a presentational sub-choice made in the
// carousel edit form, and is just noise on a badge.
export const getWidgetTypeLabel = (code: string): string => {
  if (isCarouselWidgetType(code)) return 'Carousel'
  const found = WIDGET_TYPES.find(wt => wt.code === code)
  return found ? found.label : code
}

// The widget types a merchandiser can author for the given site. An unknown or
// empty site code yields an empty list (nothing to author until a site is picked).
export const widgetTypesForSite = (siteCode: string) =>
  WIDGET_TYPES.filter(wt => (wt.sites as readonly string[]).includes(siteCode))

// The variant a freshly-created carousel starts as. All four share the same
// config, so the size/orientation is a safe default the merchandiser then
// adjusts via the selector in the carousel edit form (see CarouselEditor).
export const DEFAULT_CAROUSEL_WIDGET_TYPE: CarouselWidgetTypeCode = 'standard_landscape_carousel'

// Options for the carousel "Size & orientation" selector inside the edit form.
// Labels are the WIDGET_TYPES labels minus the redundant "Carousel" suffix (the
// selector already sits under a Carousel widget). WIDGET_TYPES stays the single
// source of truth for the label text.
export const CAROUSEL_SIZE_OPTIONS: ReadonlyArray<{ code: CarouselWidgetTypeCode; label: string }> =
  CAROUSEL_WIDGET_TYPES.map(code => ({
    code,
    label: (WIDGET_TYPES.find(wt => wt.code === code)?.label ?? code).replace(/ Carousel$/, ''),
  }))

// Options for the widget-type picker at creation. The four carousel variants
// collapse into one "Carousel" entry; selecting it creates a
// DEFAULT_CAROUSEL_WIDGET_TYPE widget whose size is then chosen in the edit
// form. Every other type maps 1:1 to its WIDGET_TYPES entry.
export const widgetTypeOptionsForSite = (siteCode: string): Array<{ code: string; label: string }> => {
  const forSite = widgetTypesForSite(siteCode)
  const options: Array<{ code: string; label: string }> = forSite
    .filter(wt => !isCarouselWidgetType(wt.code))
    .map(wt => ({ code: wt.code, label: wt.label }))
  if (forSite.some(wt => isCarouselWidgetType(wt.code))) {
    options.push({ code: DEFAULT_CAROUSEL_WIDGET_TYPE, label: 'Carousel' })
  }
  return options
}
